import { CloudOff, RotateCw } from "lucide-react";
import { Button, buttonVariants } from "./ui/button";
import { TapBoxButton } from "./TapBoxButton";

/**
 * Issue #271: the honest replacement for a swallowed failure.
 *
 * Every card on the Dashboard has an empty state — "No active goals yet",
 * "No upcoming activities scheduled", "No coach feedback yet". Those
 * are *claims about the player's data*, and a swallowed fetch
 * failure made the app assert them about data it had never received. That
 * is the same defect as `?? 0`: an unknown rendered as a plausible real
 * value. This component is what a card shows instead.
 *
 * Two deliberate choices in the copy:
 *
 * - It says what did NOT happen ("nothing has been lost"). The failure mode
 *   this issue is about is a player logging an innings on a flaky
 *   connection at a ground and then being shown zeros; the most natural
 *   reading of that screen is "the app lost my match". Saying so plainly is
 *   most of the fix.
 * - It offers a Retry the user can press. Deliberately per-card and not a
 *   global error toast: a toast that fires on every transient failure just
 *   trades a silent lie for constant noise, and it gives the user nothing
 *   to press. The action lives next to the thing that failed.
 *
 * `tapBox` (#1394, D221 G4, D354): the new look's callers pass it so "Try again" is a transparent 44px
 * tap box with the same 32px outlined button drawn inside it, and nothing moves. Unset, the markup is
 * what it was, byte for byte, for the shipped screens (D217).
 *
 * Layout note (#251): this renders as new content inside an EXISTING grid
 * cell, which is the exact shape that detonated the phone-390 overflow.
 * `min-w-0` on the flex child plus wrapping text (no fixed widths, no
 * `whitespace-nowrap`) is what keeps a 390px viewport from scrolling
 * sideways — do not replace these with a fixed max-width.
 */
export default function LoadFailureNote({ what, onRetry, testId, className = "", titleAs = "p", titleRef, tapBox = false }) {
  // `titleAs` / `titleRef` (#1234): a page whose only content is this note makes
  // the title its heading and focuses it on arrival. Unset, the output is unchanged.
  const Title = titleAs;
  return (
    <div
      className={`flex-1 flex flex-col items-center justify-center text-center py-8 min-w-0 ${className}`}
      data-testid={testId}
    >
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: "#64748B15" }}>
        <CloudOff size={20} color="#64748B" />
      </div>
      <Title ref={titleRef} tabIndex={titleRef ? -1 : undefined} className={`${titleAs !== "p" ? "nw-h1-as-p nw-focus-target " : ""}text-sm font-semibold text-slate-900 break-words`}>
        Couldn't load {what}
      </Title>
      <p className="text-xs text-slate-500 mt-1 break-words">
        Nothing has been lost — we just couldn't reach the server.
      </p>
      {onRetry && tapBox && (
        <TapBoxButton
          drawnHeight="2rem"
          drawnClassName={`${buttonVariants({ variant: "outline", size: "sm" })} h-8 text-xs`}
          spaceAbove="0.75rem"
          onClick={onRetry}
          data-testid={testId ? `${testId}-retry` : undefined}
        >
          <RotateCw size={12} className="mr-1.5" /> Try again
        </TapBoxButton>
      )}
      {onRetry && !tapBox && (
        <Button
          size="sm"
          variant="outline"
          className="mt-3 h-8 text-xs"
          onClick={onRetry}
          data-testid={testId ? `${testId}-retry` : undefined}
        >
          <RotateCw size={12} className="mr-1.5" /> Try again
        </Button>
      )}
    </div>
  );
}
